import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, Navigate } from "react-router-dom";
import Navbar from "../../../ui/Navbar";
import { AuthContext } from "../../../../context/AuthContext";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { userCountry } from "../../../../utils/getUserCountry";
import { formatPhoneNumber } from "../../../../utils/formatPhoneNumber";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

export default function ManageAdmins() {
  const { id } = useParams();
  const [institute, setInstitute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated, primaryRole } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    role: ["admin"],
    isInstructorRole: false,
    receiveEmailMessages: true,
    receiveWhatsAppMessages: false,
    instituteId: "",
  });
  const [editFormData, setEditFormData] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    isInstructorRole: false,
    receiveEmailMessages: true,
    receiveWhatsAppMessages: false,
    instituteId: "",
  });
  const [errors, setErrors] = useState({});
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });

  const breadcrumbs = [
    { name: "Home", path: "/superadmin" },
    {
      name: "Manage Institutes",
      path: `/superadmin/manage-institutes`,
      isLast: false,
    },
    {
      name: "Manage Users",
      path: `/superadmin/manage-admins/${institute?._id}`,
      isLast: true,
    },
    {
      name: institute?.name,
      path: `/superadmin/manage-admins/${institute?.name}`,
      isLast: true,
    },
  ];

  const columns = [
    { key: "firstName", label: "First Name", sortable: true },
    { key: "lastName", label: "Last Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "formattedPhone", label: "Phone Number", sortable: false },
    { key: "role", label: "Role", sortable: true },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  const fetchInstituteDetails = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${id}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch institute details");
      const data = await response.json();
      setInstitute(data);
      setFormData((prev) => ({
        ...prev,
        instituteId: data._id,
      }));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInstituteDetails();
  }, [fetchInstituteDetails]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/get-all/${institute._id}/admin`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) {
        swalError("Failed to fetch admin users");
      } else {
        const data = await response.json();

        const filteredUsers = data.filter(
            (user) =>
    user.role.includes("admin") && user.instituteId === institute?._id
);

        setUsers(filteredUsers);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [institute?._id]);

  useEffect(() => {
    if (institute?.code) {
      fetchUsers();
    }
  }, [fetchUsers, institute?.code]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";
    if (!formData.password) newErrors.password = "Password is required";
    if (formData.password.length < 8)
      newErrors.password = "Password must be at least 8 characters";
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!editFormData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!editFormData.lastName.trim())
      newErrors.lastName = "Last name is required";
    if (!editFormData.email.trim()) newErrors.email = "Email is required";
    if (!/\S+@\S+\.\S+/.test(editFormData.email))
      newErrors.email = "Email is invalid";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const cleanPhone = (val) =>
    val ? String(val).replace(/[+\-\s()]/g, "") : "";

  const checkIfEditChanged = (updatedData, userId) => {
    const original = users.find((usr) => usr._id === (userId || updatedData.id));
    if (!original) return false;
    return (
      (original.firstName || "") !== (updatedData.firstName || "") ||
      (original.lastName || "") !== (updatedData.lastName || "") ||
      (original.email || "") !== (updatedData.email || "") ||
      cleanPhone(original.phoneNumber || "") !== cleanPhone(updatedData.phoneNumber || "") ||
      Boolean(original.role?.includes("instructor")) !== Boolean(updatedData.isInstructorRole) ||
      Boolean(original.receiveEmailMessages) !== Boolean(updatedData.receiveEmailMessages) ||
      Boolean(original.receiveWhatsAppMessages) !== Boolean(updatedData.receiveWhatsAppMessages)
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePhoneChange = (value, data, event, formattedValue) => {
    const cleaned = cleanPhone(formattedValue || value || "");
    setFormData((prev) => ({
      ...prev,
      phoneNumber: cleaned,
      receiveWhatsAppMessages: cleaned
        ? !prev.phoneNumber
          ? true
          : prev.receiveWhatsAppMessages
        : false,
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      setIsEditChanged(checkIfEditChanged(updatedData, prev.id));
      return updatedData;
    });
  };

  const handleEditPhoneChange = (value, data, event, formattedValue) => {
    const cleaned = cleanPhone(formattedValue || value || "");
    setEditFormData((prev) => {
      const updatedData = {
        ...prev,
        phoneNumber: cleaned,
        receiveWhatsAppMessages: cleaned
          ? !prev.phoneNumber
            ? true
            : prev.receiveWhatsAppMessages
          : false,
      };
      setIsEditChanged(checkIfEditChanged(updatedData, prev.id));
      return updatedData;
    });
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!validateForm()) return;

    setIsSubmitting(true);
    const roleArray = formData.isInstructorRole ? ["admin", "instructor"] : ["admin"];
    const payload = { ...formData, role: roleArray }; 
    // Exclude isInstructorRole and confirmPassword from payload
    delete payload.isInstructorRole;
    delete payload.confirmPassword;
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/user/add`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response) return;

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.errors) {
          setErrors(
            errorData.errors.reduce((acc, err) => {
              acc[err.param] = err.msg;
              return acc;
            }, {})
          );
          swalError("Validation failed. Please check the form.");
          return;
        }
        throw new Error(errorData.message || "Failed to create admin");
      }

      const newUser = await response.json();
      setUsers([...users, newUser]);
      setIsModalOpen(false);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        role: ["admin"],
        isInstructorRole: false,
        receiveEmailMessages: true,
        receiveWhatsAppMessages: false,
        instituteId: formData.instituteId,
      });
      swalSuccess("Admin created successfully");
    } catch (err) {
      if (Object.keys(errors).length === 0) {
        setErrors({ form: err.message });
      }
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditAdmin = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!isEditChanged) {
      swalWarning("No changes made");
      return;
    }

    if (!validateEditForm()) return;

    setIsSubmitting(true);
    const roleArray = editFormData.isInstructorRole ? ["admin", "instructor"] : ["admin"];
    const payload = {
      firstName: editFormData.firstName,
      lastName: editFormData.lastName,
      email: editFormData.email,
      phoneNumber: editFormData.phoneNumber,
      receiveEmailMessages: editFormData.receiveEmailMessages,
      receiveWhatsAppMessages: editFormData.receiveWhatsAppMessages,
      role: roleArray,
    };
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/update/${editFormData.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response) return;

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.errors) {
          setErrors(
            errorData.errors.reduce((acc, err) => {
              acc[err.param] = err.msg;
              return acc;
            }, {})
          );
          swalError("Validation failed. Please check the form.");
          return;
        }
        throw new Error(errorData.message || "Failed to update admin");
      }

      const updatedUser = await response.json();
      setUsers(
        users.map((usr) => (usr._id === updatedUser._id ? updatedUser : usr))
      );
      setIsEditModalOpen(false);
      setEditFormData({
        id: "",
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        isInstructorRole: false,
        receiveEmailMessages: true,
        receiveWhatsAppMessages: false,
        instituteId: "",
      });
      setIsEditChanged(false);
      swalSuccess("Admin updated successfully");
    } catch (err) {
      setErrors({ form: err.message });
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId, firstName) => {
    swalConfirm(
      `Are you sure you want to delete admin "${firstName}"?`,
      "You won't be able to revert this!",
      "Yes, delete it!",
      "No, cancel!"
    ).then(async (result) => {
      if (result.isConfirmed) {
        setIsDeleting(true);
        setDeletingId(userId);
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/user/delete/${userId}`,
            {
              method: "DELETE",
              credentials: "include",
            }
          );
          if (!response) {
            swalError("Network error or failed to initiate delete request.");
            return;
          }

          if (!response.ok) {
            let errorMsg = `Failed to delete admin. Status: ${response.status}`;
            try {
              const errorData = await response.json();
              errorMsg = errorData.message || errorMsg;
            } catch (parseError) {
              swalError(
                "An error occurred while deleting the admin. Please try again later."
              );
            }
            throw new Error(errorMsg);
          }

          setUsers((prevUsers) =>
            prevUsers.filter((user) => user._id !== userId)
          );
          swalSuccess("Admin deleted successfully");
        } catch (err) {
          swalError(
            err.message || "An unexpected error occurred during deletion."
          );
        } finally {
          setIsDeleting(false);
          setDeletingId(null);
        }
      }
    });
  };

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/login" replace />;
  }

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!institute) return <div className="not-found">Institute not found</div>;

  const enhancedUsers = users.map((user) => ({
    ...user,
    role: user.role.join(", "),
    formattedPhone: user.phoneNumber ? formatPhoneNumber(user.phoneNumber) : "-",
    edit: (
      <button
        className="edit-btn"
        onClick={() => {
          const phone = cleanPhone(user.phoneNumber || "");
          const hasPhone = Boolean(phone);
          setEditFormData({
            id: user._id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phoneNumber: phone,
            isInstructorRole: user.role.includes("instructor"),
            receiveEmailMessages:
              user.receiveEmailMessages !== undefined
                ? Boolean(user.receiveEmailMessages)
                : true,
            receiveWhatsAppMessages:
              user.receiveWhatsAppMessages !== undefined
                ? Boolean(user.receiveWhatsAppMessages) && hasPhone
                : hasPhone,
            instituteId: user.instituteId,
          });
          setIsEditModalOpen(true);
          setIsEditChanged(false);
          setErrors({});
        }}
        disabled={isDeleting}
      >
        <i className="fa-solid fa-pen-to-square"></i>
      </button>
    ),
    delete: (
      <button
        className="delete-btn"
        onClick={() => handleDelete(user._id, user.firstName)}
        disabled={isDeleting && deletingId === user._id}
      >
        {isDeleting && deletingId === user._id ? (
          <Loader
            style={{
              fontSize: "1em",
              color: "currentColor",
              top: "0",
              left: "0",
            }}
          />
        ) : (
          <i className="fa-solid fa-trash-can"></i>
        )}
      </button>
    ),
  }));

  return (
    <>
      <Navbar
        pageName={`Manage Institute: ${institute.name}`}
        activePage="Manage Users"
        createButtons={[
          {
            text: "Add Admin",
            onClick: () => {
              setFormData((prev) => ({
                ...prev,
                receiveEmailMessages: true,
                receiveWhatsAppMessages: Boolean(prev.phoneNumber),
              }));
              setIsModalOpen(true);
            },
          },
        ]}
        onCreateClick={() => {
          setFormData((prev) => ({
            ...prev,
            receiveEmailMessages: true,
            receiveWhatsAppMessages: Boolean(prev.phoneNumber),
          }));
          setIsModalOpen(true);
          setErrors({});
        }}
        breadcrumbs={breadcrumbs}
      />
      <div className="manage-users-container">
        <div className="table-section">
          <Table
            data={enhancedUsers}
            columns={columns}
            caption="Institute Admins"
          />
        </div>

        {/* Create Admin Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => !isSubmitting && setIsModalOpen(false)}
        >
          <h2>Create New Admin</h2>
          <form onSubmit={handleCreateAdmin} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> First
                  Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter admin's first name"
                />
                {errors.firstName && (
                  <span className="error">{errors.firstName}</span>
                )}
              </div>
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Last
                  Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter admin's last name"
                />
                {errors.lastName && (
                  <span className="error">{errors.lastName}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter admin's email address"
              />
              {errors.email && <span className="error">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label>
                Phone Number{" "}
                <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 400 }}>
                  (Optional)
                </span>
              </label>
              <PhoneInput
                country={userCountry || "in"}
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                inputStyle={{
                  height: "40px",
                  fontSize: "14px",
                  width: "100%",
                  paddingLeft: "48px",
                  borderRadius: "8px",
                  borderColor: "#e2e8f0",
                }}
                buttonStyle={{
                  height: "40px",
                  borderRadius: "8px 0 0 8px",
                  borderColor: "#e2e8f0",
                }}
                containerStyle={{
                  width: "100%",
                }}
                enableSearch={true}
                disableSearchIcon={true}
                placeholder="Enter phone number"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email Notifications</label>
                <label className="solution-toggle">
                  <input
                    name="receiveEmailMessages"
                    type="checkbox"
                    checked={formData.receiveEmailMessages}
                    onChange={handleInputChange}
                  />
                  <span className="toggle-track">
                    <span className="toggle-indicator"></span>
                  </span>
                  <span className="toggle-label">
                    {formData.receiveEmailMessages ? "Enabled" : "Disabled"}
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>WhatsApp Notifications</label>
                <label
                  className="solution-toggle"
                  style={!formData.phoneNumber ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                  title={!formData.phoneNumber ? "Phone number is required for WhatsApp notifications" : ""}
                >
                  <input
                    name="receiveWhatsAppMessages"
                    type="checkbox"
                    checked={formData.receiveWhatsAppMessages}
                    onChange={handleInputChange}
                    disabled={!formData.phoneNumber}
                  />
                  <span className="toggle-track">
                    <span className="toggle-indicator"></span>
                  </span>
                  <span className="toggle-label">
                    {formData.receiveWhatsAppMessages ? "Enabled" : "Disabled"}
                  </span>
                </label>
                {!formData.phoneNumber && (
                  <small style={{ color: "#888", fontSize: "0.75rem", display: "block", marginTop: "4px" }}>
                    Enter phone number to enable
                  </small>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Assign Instructor Role</label>
              <label className="solution-toggle">
                <input
                  name="isInstructorRole"
                  type="checkbox"
                  checked={formData.isInstructorRole}
                  onChange={handleInputChange}
                />
                <span className="toggle-track">
                  <span className="toggle-indicator"></span>
                </span>
                <span className="toggle-label">{formData.isInstructorRole ? "Yes" : "No"}</span>
              </label>
            </div>

            <div className="form-group password-field">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Password
              </label>
              <input
                type={passwordVisibility.password ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Create a password (min 8 characters)"
              />
              <span
                className="toggle-password"
                onClick={() => togglePasswordVisibility("password")}
              >
                <i
                  className={`fa-regular ${
                    passwordVisibility.password ? "fa-eye-slash" : "fa-eye"
                  }`}
                />
              </span>
              {errors.password && (
                <span className="error">{errors.password}</span>
              )}
            </div>

            <div className="form-group password-field">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Confirm
                Password
              </label>
              <input
                type={passwordVisibility.confirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                placeholder="Re-enter the password"
              />
              <span
                className="toggle-password"
                onClick={() => togglePasswordVisibility("confirmPassword")}
              >
                <i
                  className={`fa-regular ${
                    passwordVisibility.confirmPassword
                      ? "fa-eye-slash"
                      : "fa-eye"
                  }`}
                />
              </span>
              {errors.confirmPassword && (
                <span className="error">{errors.confirmPassword}</span>
              )}
            </div>
            {errors.form && <div className="error-message">{errors.form}</div>}
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader
                  style={{
                    fontSize: "1em",
                    color: "white",
                    top: "0",
                    left: "0",
                  }}
                />
              ) : (
                <>
                  <i className="fa-solid fa-plus"></i> Create Admin
                </>
              )}
            </button>
          </form>
        </Modal>

        {/* Edit Admin Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => !isSubmitting && setIsEditModalOpen(false)}
        >
          <h2>Edit Admin</h2>
          <form onSubmit={handleEditAdmin} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> First
                  Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={editFormData.firstName}
                  onChange={handleEditInputChange}
                  required
                  placeholder="Edit admin's first name"
                />
                {errors.firstName && (
                  <span className="error">{errors.firstName}</span>
                )}
              </div>
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Last
                  Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={editFormData.lastName}
                  onChange={handleEditInputChange}
                  required
                  placeholder="Edit admin's last name"
                />
                {errors.lastName && (
                  <span className="error">{errors.lastName}</span>
                )}
              </div>
            </div>


            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Email
              </label>
              <input
                type="email"
                name="email"
                value={editFormData.email}
                onChange={handleEditInputChange}
                required
                placeholder="Edit admin's email address"
              />
              {errors.email && <span className="error">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label>
                Phone Number{" "}
                <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 400 }}>
                  (Optional)
                </span>
              </label>
              <PhoneInput
                country={userCountry || "in"}
                value={editFormData.phoneNumber}
                onChange={handleEditPhoneChange}
                inputStyle={{
                  height: "40px",
                  fontSize: "14px",
                  width: "100%",
                  paddingLeft: "48px",
                  borderRadius: "8px",
                  borderColor: "#e2e8f0",
                }}
                buttonStyle={{
                  height: "40px",
                  borderRadius: "8px 0 0 8px",
                  borderColor: "#e2e8f0",
                }}
                containerStyle={{
                  width: "100%",
                }}
                enableSearch={true}
                disableSearchIcon={true}
                placeholder="Enter phone number"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email Notifications</label>
                <label className="solution-toggle">
                  <input
                    name="receiveEmailMessages"
                    type="checkbox"
                    checked={editFormData.receiveEmailMessages}
                    onChange={handleEditInputChange}
                  />
                  <span className="toggle-track">
                    <span className="toggle-indicator"></span>
                  </span>
                  <span className="toggle-label">
                    {editFormData.receiveEmailMessages ? "Enabled" : "Disabled"}
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>WhatsApp Notifications</label>
                <label
                  className="solution-toggle"
                  style={!editFormData.phoneNumber ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                  title={!editFormData.phoneNumber ? "Phone number is required for WhatsApp notifications" : ""}
                >
                  <input
                    name="receiveWhatsAppMessages"
                    type="checkbox"
                    checked={editFormData.receiveWhatsAppMessages}
                    onChange={handleEditInputChange}
                    disabled={!editFormData.phoneNumber}
                  />
                  <span className="toggle-track">
                    <span className="toggle-indicator"></span>
                  </span>
                  <span className="toggle-label">
                    {editFormData.receiveWhatsAppMessages ? "Enabled" : "Disabled"}
                  </span>
                </label>
                {!editFormData.phoneNumber && (
                  <small style={{ color: "#888", fontSize: "0.75rem", display: "block", marginTop: "4px" }}>
                    Enter phone number to enable
                  </small>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Assign Instructor Role</label>
              <label className="solution-toggle">
                <input
                  name="isInstructorRole"
                  type="checkbox"
                  checked={editFormData.isInstructorRole}
                  onChange={handleEditInputChange}
                />
                <span className="toggle-track">
                  <span className="toggle-indicator"></span>
                </span>
                <span className="toggle-label">{editFormData.isInstructorRole ? "Yes" : "No"}</span>
              </label>
            </div>
            {errors.form && <div className="error-message">{errors.form}</div>}
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged || isSubmitting}
            >
              {isSubmitting ? (
                <Loader
                  style={{
                    fontSize: "1em",
                    color: "white",
                    top: "0",
                    left: "0",
                  }}
                />
              ) : (
                <>
                  <i className="fa-solid fa-floppy-disk"></i> Save
                </>
              )}
            </button>
          </form>
        </Modal>
      </div>
    </>
  );
}
